import { findPlanById } from '@/data/services/plan/plan-service-utils'
import AccountAvatarApp, {
  AccountAvatarSize
} from '@/presentation/components/ui/data-display/account-avatar-app'
import { ValidAccountState } from '@/presentation/contexts/auth'
import { generateUserNameSummarized } from '@/presentation/helpers/account-profile-helper'
import useAccount from '@/presentation/hooks/auth/use-account'
import { useAvailablePlans } from '@/presentation/hooks/plan/use-available-plans'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import { AppBar, Box, Dialog, IconButton, List, Toolbar, Typography, useTheme } from '@mui/material'
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import PlanChip from '../../../chip/plan-chip'
import DialogMenuTransition from '../../../transition/dialog-menu-transition'

import SxHeader from '../../sx-header'
import { MenuList } from '../shared/menu-section-list'

type MobileUserMenuProps = {
  open: boolean
  setOpen: (open: boolean) => void
  handleClose: () => void
}

export default function MobileUserMenu({ open, setOpen, handleClose }: MobileUserMenuProps) {
  const theme = useTheme()
  const location = useLocation()
  const [account] = useAccount() as ValidAccountState
  const { data: availablePlans = [] } = useAvailablePlans()
  const plan = account?.passPlan
    ? findPlanById(availablePlans, account.passPlan.id as number)
    : undefined

  useEffect(() => {
    setOpen(false)
  }, [location, setOpen])

  const paperProps = {
    elevation: 0,
    sx: {
      backgroundColor: 'background.paper',

      '& .MuiList-root': {
        display: 'flex',
        flexDirection: 'column',
        padding: 0,

        '& .MuiTypography-root': {
          fontSize: theme.typography.pxToRem(16)
        },

        '& .MuiTypography-caption': {
          fontSize: theme.typography.pxToRem(12)
        },

        '& .MuiListItemButton-root': {
          margin: 0,
          flexGrow: 0
        }
      }
    }
  }

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      sx={{ display: { xs: 'inherit', md: 'none' } }}
      disableScrollLock
      slots={{
        transition: DialogMenuTransition
      }}
      slotProps={{
        paper: paperProps
      }}
    >
      <AppBar component="header" sx={SxHeader.appBarNoShadow}>
        <Toolbar
          sx={{
            height: theme.spacing(6),
            flexFlow: 1,
            alignItems: 'end',
            justifyContent: 'end'
          }}
          variant="dense"
        >
          <IconButton
            size="small"
            aria-controls={open ? 'close-account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleClose}
          >
            <CloseOutlinedIcon />
          </IconButton>
        </Toolbar>

        <List
          component="nav"
          sx={{
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto'
          }}
        >
          {/* User Profile Section */}
          <Box className="flex flex-row gap-4 pt-0 p-4" component="li">
            <AccountAvatarApp size={AccountAvatarSize.medium} />
            <Box className="flex flex-col items-start gap-1">
              <Typography variant="body1" color="grey.700" className="ellipsis-1-line capitalize">
                {generateUserNameSummarized(account.displayName)}
              </Typography>
              <Typography variant="body1" color="grey.500">
                {account.email}
              </Typography>
              <PlanChip plan={plan} />
            </Box>
          </Box>

          <MenuList account={account} variant="mobile" handleClose={handleClose} />
        </List>
      </AppBar>
    </Dialog>
  )
}
